/**
 * Recursos API para Suscripciones de Pagos Recurrentes
 */

/**
 * Mapear estado de suscripción de API a formato frontend
 */
const mapSubscriptionStatus = (status) => {
  if (!status) return 'Active';
  
  // Normalizar el status a lowercase para el mapeo
  const normalizedStatus = status.toLowerCase();
  
  const statusMap = {
    'trial': 'Trial',
    'active': 'Active',
    'past_due': 'PastDue',
    'pastdue': 'PastDue',
    'paused': 'Paused',
    'cancelled': 'Cancelled',
    'expired': 'Expired'
  };
  
  return statusMap[normalizedStatus] || status;
};

/**
 * Mapear frecuencia de facturación
 */
const mapBillingFrequency = (frequency) => {
  const frequencyMap = {
    'daily': 'Daily',
    'weekly': 'Weekly',
    'monthly': 'Monthly',
    'quarterly': 'Quarterly',
    'yearly': 'Yearly'
  };
  
  return frequencyMap[frequency] || frequency;
};

/**
 * Transformar datos de suscripción de API a formato frontend
 */
export const mapSubscriptionFromApi = (subscription) => {
  if (!subscription) return null;
  
  return {
    subscriptionId: subscription.subscription_id,
    customerId: subscription.customer_id,
    planId: subscription.plan_id,
    paymentMethodId: subscription.payment_method_id,
    status: mapSubscriptionStatus(subscription.status),
    startDate: subscription.start_date,
    trialEndDate: subscription.trial_end_date,
    currentPeriodStart: subscription.current_period_start,
    currentPeriodEnd: subscription.current_period_end,
    nextBillingDate: subscription.next_billing_date,
    endDate: subscription.end_date,
    billingCyclesCompleted: subscription.billing_cycles_completed,
    billingCyclesRemaining: subscription.billing_cycles_remaining,
    lastAmount: subscription.last_amount,
    nextAmount: subscription.next_amount,
    nextAmountDescription: subscription.next_amount_description,
    metadata: subscription.metadata,
    externalReference: subscription.external_reference,
    createdAt: subscription.created_at,
    updatedAt: subscription.updated_at,
    cancelledAt: subscription.cancelled_at,
    customer: subscription.customer ? {
      email: subscription.customer.email,
      name: subscription.customer.name || '',
      fullName: subscription.customer.name || subscription.customer.email || 'Sin nombre',
      phone: subscription.customer.phone,
      documentType: subscription.customer.document_type,
      documentNumber: subscription.customer.document || subscription.customer.document_number,
    } : (subscription.email ? {
      // Si no hay objeto customer pero hay email directamente (es un customer directo de la API)
      email: subscription.email,
      name: subscription.name || '',
      fullName: subscription.name || subscription.email || 'Sin nombre',
      phone: subscription.phone,
      documentType: subscription.document_type,
      documentNumber: subscription.document || subscription.document_number,
    } : null),
    plan: subscription.plan ? {
      name: subscription.plan.name,
      description: subscription.plan.description,
      amount: subscription.plan.amount,
      currency: subscription.plan.currency,
      billingFrequency: mapBillingFrequency(subscription.plan.billing_frequency),
      pricingType: subscription.plan.pricing_type,
      billingDay: subscription.plan.billing_day,
      status: subscription.plan.status,
      frequency: mapBillingFrequency(subscription.plan.billing_frequency),
      bulkUpdateLastProcessedAt: subscription.plan.bulk_update_last_processed_at,
    } : null,
    paymentMethod: subscription.payment_method ? {
      brand: subscription.payment_method.brand,
      lastFourDigits: subscription.payment_method.last_four_digits,
      expirationMonth: subscription.payment_method.expiration_month,
      expirationYear: subscription.payment_method.expiration_year,
      cardholderName: subscription.payment_method.cardholder_name,
    } : null,
  };
};

/**
 * Obtener lista de suscripciones con paginación
 */
export const getSubscriptions = (token) => (payload) => ({
  endpoint: '/api/v1/recurring-payments/subscriptions',
  method: 'GET',
  params: payload,
  headers: {
    'X-Authentication-Token': token,
  },
  cache: false,
  proxy: false,
  preserveErrorObject: true,
  parseResponse: (response) => {
    const apiData = response.data?.data || {};
    const subscriptionsArray = Array.isArray(apiData.data) ? apiData.data : [];
    const mappedData = subscriptionsArray.map(mapSubscriptionFromApi);
    const pagination = apiData.pagination || apiData;
    
    return {
      data: mappedData,
      pagination: {
        total_count: parseInt(pagination.total_count) || parseInt(pagination.totalCount) || 0,
        limit: parseInt(pagination.limit) || 10,
        offset: parseInt(pagination.offset) || 0,
      },
    };
  },
});

/**
 * Obtener detalle de una suscripción
 */
export const getSubscriptionById = (subscriptionId, token) => ({
  endpoint: `/api/v1/recurring-payments/subscriptions/${subscriptionId}`,
  method: 'GET',
  headers: {
    'X-Authentication-Token': token,
  },
  cache: false,
  proxy: false,
  preserveErrorObject: true,
  parseResponse: (response) => {
    const apiData = response.data?.data;
    return mapSubscriptionFromApi(apiData);
  },
});

/**
 * Actualizar una suscripción existente
 */
export const updateSubscription = (subscriptionId, data, token) => ({
  endpoint: `/api/v1/recurring-payments/subscriptions/${subscriptionId}`,
  method: 'PUT',
  data,
  headers: {
    'X-Authentication-Token': token,
  },
  proxy: false,
  preserveErrorObject: true,
  parseResponse: (response) => {
    const apiData = response.data?.data;
    return mapSubscriptionFromApi(apiData);
  },
});

/**
 * Pausar una suscripción
 */
export const pauseSubscription = (subscriptionId, data, token) => ({
  endpoint: `/api/v1/recurring-payments/subscriptions/${subscriptionId}`,
  method: 'PUT',
  data: {
    status: 'paused',
    ...data,
  },
  headers: {
    'X-Authentication-Token': token,
  },
  proxy: false,
  preserveErrorObject: true,
  parseResponse: (response) => {
    const apiData = response.data?.data;
    return mapSubscriptionFromApi(apiData);
  },
});

/**
 * Cancelar una suscripción
 */
export const cancelSubscription = (subscriptionId, data, token) => ({
  endpoint: `/api/v1/recurring-payments/subscriptions/${subscriptionId}`,
  method: 'DELETE',
  data,
  headers: {
    'X-Authentication-Token': token,
  },
  proxy: false,
  preserveErrorObject: true,
  parseResponse: (response) => {
    const apiData = response.data?.data;
    return mapSubscriptionFromApi(apiData);
  },
});

/**
 * Reactivar una suscripción
 */
export const bulkSubscriptionAction = (data, token) => ({
  endpoint: '/api/v1/recurring-payments/subscriptions/bulk-action',
  method: 'POST',
  data,
  headers: {
    'X-Authentication-Token': token,
  },
  proxy: false,
  preserveErrorObject: true,
  parseResponse: (response) => {
    const apiData = response.data?.data || response.data || {};
    return {
      processed: apiData.processed || 0,
      succeeded: apiData.succeeded || 0,
      skipped: apiData.skipped || 0,
      failed: apiData.failed || 0,
      truncated: !!apiData.truncated,
      totalMatched: apiData.total_matched || 0,
      results: (apiData.results || []).map((item) => ({
        subscriptionId: item.subscription_id,
        email: item.email,
        outcome: item.outcome,
        reason: item.reason,
        errorCode: item.error_code,
      })),
    };
  },
});

export const reactivateSubscription = (subscriptionId, token) => ({
  endpoint: `/api/v1/recurring-payments/subscriptions/${subscriptionId}`,
  method: 'PUT',
  data: {
    status: 'active',
  },
  headers: {
    'X-Authentication-Token': token,
  },
  proxy: false,
  preserveErrorObject: true,
  parseResponse: (response) => {
    const apiData = response.data?.data;
    return mapSubscriptionFromApi(apiData);
  },
});

/**
 * Enviar notificación por email al customer
 */
export const sendNotification = (action, customerId, subscriptionId, token) => ({
  endpoint: '/api/v1/recurring-payments/notifications',
  method: 'POST',
  data: {
    action,
    customer_id: customerId,
    subscription_id: subscriptionId,
  },
  headers: {
    'X-Authentication-Token': token,
  },
  proxy: false,
  preserveErrorObject: true,
});

export const exportSubscribersReport = (token) => (payload) => ({
  endpoint: '/api/v1/recurring-payments/reports/subscriptions/export',
  method: 'GET',
  params: payload,
  headers: {
    'X-Authentication-Token': token,
  },
  proxy: false,
  responseType: 'blob',
});

/**
 * Obtener los cobros de una suscripción
 */
export const getChargesBySubscription = (subscriptionId, token) => ({
  endpoint: '/api/v1/recurring-payments/charges',
  method: 'GET',
  params: {
    subscription_id: subscriptionId,
    limit: 10,
    offset: 0,
  },
  headers: {
    'X-Authentication-Token': token,
  },
  proxy: false,
  preserveErrorObject: true,
  parseResponse: (response) => {
    const apiData = response.data?.data || {};
    const chargesArray = Array.isArray(apiData.data) ? apiData.data : (Array.isArray(apiData) ? apiData : []);
    
    return chargesArray.map(charge => ({
      chargeId: charge.charge_id,
      subscriptionId: charge.subscription_id,
      amount: charge.amount,
      currency: charge.currency,
      status: mapChargeStatus(charge.status),
      createdAt: charge.created_at,
      updatedAt: charge.updated_at,
      failureReason: charge.failure_reason,
      paymentMethodLast4: charge.payment_method_last4,
      paymentMethodBrand: charge.payment_method_brand,
    }));
  },
});

const mapChargeStatus = (status) => {
  if (!status) return null;
  const statusLower = status.toLowerCase();
  const statusMap = {
    'pending': 'pending',
    'processing': 'processing',
    'succeeded': 'succeeded',
    'failed': 'failed',
    'cancelled': 'cancelled',
    'pastdue': 'pastdue',
    'past_due': 'pastdue',
    'skippedzeroamount': 'skippedzeroamount',
    'skippednullamount': 'skippednullamount',
  };
  return statusMap[statusLower] || statusLower;
};

